import { Controller } from "@hotwired/stimulus"
import {
  connectImmediateSortSync,
  disconnectImmediateSortSync,
  frameElements,
  markTableFrameLoaded,
  matchesTurboFrameFetch,
  revertOptimisticSort,
  showTableSkeleton,
  turboFrameHeader,
} from "./table_frame_support"

export default class extends Controller {
  static targets = ["frame", "skeletonTemplate"]
  static values = {
    tableFrameId: String,
    tableFrameIds: { type: Array, default: [] },
    parentFrameIds: { type: Array, default: [] },
    filterActionIncludes: String,
  }

  connect() {
    this.warnWhenMisconfigured()
    connectImmediateSortSync(this)
    this.boundOnTableFetchError = this.onTableFetchError.bind(this)
    this.boundOnRetry = this.onRetry.bind(this)
    this.element.addEventListener("click", this.boundOnRetry)
    frameElements(this).forEach((frame) => {
      frame.addEventListener(
        "turbo:fetch-request-error",
        this.boundOnTableFetchError,
      )
      frame.addEventListener("turbo:frame-missing", this.boundOnTableFetchError)
    })
  }

  disconnect() {
    disconnectImmediateSortSync(this)
    if (this.boundOnRetry) {
      this.element.removeEventListener("click", this.boundOnRetry)
    }
    if (this.boundOnTableFetchError) {
      frameElements(this).forEach((frame) => {
        frame.removeEventListener(
          "turbo:fetch-request-error",
          this.boundOnTableFetchError,
        )
        frame.removeEventListener(
          "turbo:frame-missing",
          this.boundOnTableFetchError,
        )
      })
    }
  }

  onRetry(event) {
    const retry = event.target.closest("[data-educator-home-lazy-frame-retry]")
    if (!retry || !this.element.contains(retry)) return

    const frame = retry.closest("turbo-frame")
    if (!frame || !this.tableFrameIdList.includes(frame.id)) return

    const url = frame.getAttribute("src")
    if (!url) return

    event.preventDefault()
    showTableSkeleton(this, frame)
    frame.setAttribute("aria-busy", "true")
    frame.removeAttribute("src")
    frame.setAttribute("src", url)
  }

  get tableFrameIdList() {
    if (this.hasTableFrameIdsValue && this.tableFrameIdsValue.length > 0) {
      return this.tableFrameIdsValue
    }
    if (this.hasTableFrameIdValue) return [this.tableFrameIdValue]
    return []
  }

  warnWhenMisconfigured() {
    if (typeof process !== "undefined" && process.env?.NODE_ENV === "production") {
      return
    }

    if (this.tableFrameIdList.length === 0) {
      console.warn(
        "[educator-home-page--table-frame] Missing tableFrameId value; table fetch/skeleton handling will no-op.",
        this.element,
      )
    }

    if (!this.filterActionIncludesValue) {
      console.warn(
        "[educator-home-page--table-frame] Missing filterActionIncludes value; filter form submits will not show the table skeleton.",
        this.element,
      )
    }
  }

  onTableFetchError(event) {
    const frame = event.target
    if (!frameElements(this).includes(frame)) return

    revertOptimisticSort(this, frame)
  }

  filterChanged() {
    const pendingFrameIds = this.pendingFilterSkeletonFrameIds()

    if (pendingFrameIds.length > 0) {
      pendingFrameIds.forEach((frameId) => {
        showTableSkeleton(this, this.frameElementForId(frameId))
      })
      delete this.element.dataset.ccaiFilterSkeletonFrameIds
      return
    }

    showTableSkeleton(this)
  }

  pendingFilterSkeletonFrameIds() {
    const raw = this.element.dataset.ccaiFilterSkeletonFrameIds
    if (!raw) return []

    try {
      return JSON.parse(raw)
    } catch {
      return []
    }
  }

  beforeTableFetch(event) {
    const frameHeader = turboFrameHeader(event.detail?.fetchOptions?.headers)
    const matchingFrame = this.frameElementForId(frameHeader)
    const matchesTable =
      matchingFrame != null &&
      matchesTurboFrameFetch(event, this.tableFrameIdList)
    const matchesFilterFormSubmit = this.matchesFilterFormSubmit(
      event,
      frameHeader,
    )

    if (matchesTable) {
      showTableSkeleton(this, matchingFrame)
      return
    }

    if (matchesFilterFormSubmit) {
      const pendingFrameIds = this.pendingFilterSkeletonFrameIds()
      if (pendingFrameIds.length > 0) {
        pendingFrameIds.forEach((frameId) => {
          showTableSkeleton(this, this.frameElementForId(frameId))
        })
      } else {
        showTableSkeleton(this)
      }
    }
  }

  frameElementForId(frameId) {
    if (!frameId) return null

    return frameElements(this).find((frame) => frame.id === frameId) || null
  }

  matchesFilterFormSubmit(event, frameHeader) {
    const form = event.target
    if (form?.tagName !== "FORM") return false
    if (!this.element.contains(form)) return false
    if (
      !this.tableFrameIdList.includes(frameHeader) &&
      !this.parentFrameIdsValue.includes(frameHeader)
    ) {
      return false
    }

    const action = form.getAttribute("action") || ""
    if (!action.includes(this.filterActionIncludesValue)) return false

    if (
      this.filterActionIncludesValue ===
      "calibrate_curricular_action_items_card"
    ) {
      return action.includes("calibrate_curricular_action_items_card/query")
    }

    return true
  }

  tableLoaded(event) {
    markTableFrameLoaded(this, event?.target)
  }
}
